'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  buildResultsPerPage,
  loadPaginationActions,
} from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { DEFAULT_SEARCH_PAGE_SIZE } from '@lib/constants/coveo';
import { useFlags } from '@lib/flags/amplitude/client';
import { Flags } from '@lib/flags/flagConstants';
import { useController } from '@lib/hooks/useController';
import { useMemo, useState } from 'react';
import { Dropdown, DropdownOption } from 'ui-components';
import { useEngine } from '../EngineContext';
import {
  getResultsPerPageFromLocalStorage,
  setResultsPerPageToLocalStorage,
} from '../utils/resultsPerPageStorage';
import { ResultsPerPageProps } from './ResultsPerPage.types';

const PAGE_SIZE_OPTIONS = [24, 48, 96];

export const ResultsPerPage = ({ className }: ResultsPerPageProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();
  const { flags } = useFlags();
  const flag = flags[Flags.INCREASE_DEFAULT_RESULTS_PER_PAGE];
  const defaultPageSize = flag?.payload?.pageSize || DEFAULT_SEARCH_PAGE_SIZE;

  const resultsLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.results-per-page')
      ?.value ?? 'Results';

  const resultsPerPageAriaLabel =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.results-per-page-aria-label'
    )?.value ?? '';

  // Compute the initial value once on mount to prevent controller re-creation.
  // Re-creating the controller dispatches registerNumberOfResults which sets
  // defaultNumberOfResults = numberOfResults, causing Coveo to omit
  // numberOfResults from the URL hash (it drops values equal to the default).
  // This would make the user's choice unrecoverable from the URL alone.
  const [initialNumberOfResults] = useState(
    () => getResultsPerPageFromLocalStorage() ?? defaultPageSize
  );

  const eng = useEngine();
  const controller = useMemo(
    () =>
      buildResultsPerPage(eng, {
        initialState: { numberOfResults: initialNumberOfResults },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const state = useController(controller);

  const options: DropdownOption[] = PAGE_SIZE_OPTIONS.map((size) => ({
    label: String(size),
    value: String(size),
  }));

  const selectedOption =
    options.find((opt) => opt.value === String(state.numberOfResults)) ??
    options[0];

  const handleSelect = (option: DropdownOption) => {
    const numberOfResults = parseInt(option.value, 10);
    controller.set(numberOfResults);
    // Update defaultNumberOfResults so SPA hash-change navigation doesn't revert the
    // setting via restoreSearchParameters. registerNumberOfResults is a pure Redux
    // state action — it does NOT dispatch executeSearch, so no second search fires.
    // Trade-off: once the default equals the user's choice, Coveo omits numberOfResults
    // from the URL hash, so the preference is persisted via localStorage only and won't
    // survive copy-pasted or shared URLs. Page-size is treated as a personal preference
    // rather than a shareable filter parameter.
    eng.dispatch(
      loadPaginationActions(eng).registerNumberOfResults(numberOfResults)
    );
    setResultsPerPageToLocalStorage(numberOfResults);
  };

  return (
    <div className={className}>
      <div className="tiny-text mb-2 text-grey-600">{resultsLabel}</div>
      <Dropdown
        id="results-per-page"
        options={options}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        isDarkTheme={false}
        ariaLabel={
          resultsPerPageAriaLabel ||
          `Show ${selectedOption.label} results per page`
        }
        className="w-16 min-w-16 [&_[role=option]]:m-0.5 [&_[role=option]]:p-1.5 [&_button]:p-2"
      />
    </div>
  );
};
